import { useEffect, useState } from "react";
import _ from "lodash";

// Logger
import { logDebug, logError, logInfo, logWarn } from "src/services/logger";

// Store access
import { store } from "src/store";

// Types
import { MediaTypes } from "src/types/check-devices";
import { type ReturnData } from "src/types";

// Actions
import { setMediaStream, updateAvailableDevices, updateMultipleDevices } from "src/store/media/reducer";
import { useAppDispatch, useAppSelector } from "src/store/hooks";

export let MediaManagerInstance: {
  // Functions
  createMediaStream: ({ inputDeviceId, cameraDeviceId }: MediaManagerConfig) => Promise<ReturnData>;
  checkDevices: () => Promise<ReturnData>;
  stopLocalMedia: () => void;
  getTracks: (types?: Array<MediaTypes>) => Array<MediaStreamTrack>;
  getMediaStream: (types?: Array<MediaTypes>) => MediaStream | null;
  changeDeviceStream: (type: MediaTypes, deviceId: string) => Promise<ReturnData>;
};

type MediaManagerConfig = { inputDeviceId: string; cameraDeviceId: string };

export function createMediaManager() {
  logInfo("MediaManager: iniciado");

  // Local variables
  let ReturnMediaStream: MediaStream | undefined;

  let availableDevices: MediaDeviceInfo[] = [];
  let mappedTracks: Array<{
    id: string;
    kind: MediaTypes;
  }> = [];

  // Dispatch
  const { dispatch } = store;

  // Private functions
  async function getAllUserMedia({ inputDeviceId, cameraDeviceId }: MediaManagerConfig): Promise<{
    success: boolean;
    stream?: MediaStream;
    message?: string;
  }> {
    const foundDevices = availableDevices;

    const defaultInput = inputDeviceId ? foundDevices.find((d) => d.deviceId === inputDeviceId && d.kind === "audioinput") : foundDevices.find((d) => d.deviceId === "default");
    const defaultCamera = cameraDeviceId ? foundDevices.find((d) => d.deviceId === cameraDeviceId && d.kind === "videoinput") : foundDevices.find((d) => d.kind === "videoinput");

    const config: {
      video: any;
      audio: any;
    } = {
      video: {
        width: 1280,
        height: 720,
        frameRate: { ideal: 24, min: 15, max: 24 },
      },
      audio: true,
    };

    if (defaultCamera) config.video.deviceId = { exact: defaultCamera.deviceId };
    if (defaultInput) config.audio = { deviceId: { exact: defaultInput.deviceId } };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(config);

      return { success: true, stream };
    } catch (e: any) {
      return { success: false, message: e.message };
    }
  }

  // Exposed methods
  const checkDevices = async (): Promise<ReturnData> => {
    logDebug("MediaManager: verificando dispositivos");

    const defaultErrorMessage = "Falha ao buscar dispositivos disponíveis";

    try {
      availableDevices = await navigator.mediaDevices.enumerateDevices();
      dispatch(updateAvailableDevices(availableDevices));

      logDebug("MediaManager: dispositivos verificados", { deviceCount: availableDevices.length });

      return { success: true, data: availableDevices };
    } catch (ex: any) {
      logError("MediaManager: erro ao verificar dispositivos", ex);

      return {
        success: false,
        message: `${defaultErrorMessage}: ${ex.message}`,
      };
    }
  };

  const stopLocalMedia = () => {
    logDebug("MediaManager: parando tracks");

    // Map only tracks
    const camAndMicTrackIds = mappedTracks.filter((m) => [MediaTypes.MIC, MediaTypes.CAM].includes(m.kind)).map((m) => m.id);

    // Get tracks
    const tracks = ReturnMediaStream?.getTracks().filter((t) => camAndMicTrackIds.includes(t.id));

    if (tracks) {
      // Stop tracks
      tracks?.forEach((t) => t.stop());

      // Remove from main map
      mappedTracks = [...mappedTracks.filter((m) => !camAndMicTrackIds.includes(m.id))];
    }

    return true;
  };

  const getTracks = (types?: Array<MediaTypes>): Array<MediaStreamTrack> => {
    if (ReturnMediaStream) {
      const allTracks = ReturnMediaStream.getTracks();

      if (types) {
        const filteredIds = mappedTracks.filter((m) => types.includes(m.kind)).map((m) => m.id);

        return allTracks.filter((t) => filteredIds.includes(t.id));
      } else return allTracks;
    }

    return [];
  };

  const getMediaStream = (types?: Array<MediaTypes>): MediaStream | null => {
    if (ReturnMediaStream) {
      const newMediaStream = new MediaStream();
      const allTracks = ReturnMediaStream.getTracks();

      if (types) {
        const filteredIds = mappedTracks.filter((m) => types.includes(m.kind)).map((m) => m.id);

        allTracks.filter((t) => filteredIds.includes(t.id)).forEach((t) => newMediaStream.addTrack(t));
      } else allTracks.forEach((t) => newMediaStream.addTrack(t));

      return newMediaStream;
    }

    return null;
  };

  const changeDeviceStream = async (type: MediaTypes, deviceId: string): Promise<ReturnData> => {
    const mediaDevicesConfig: any = {};

    if (type === MediaTypes.CAM)
      mediaDevicesConfig.video = {
        deviceId: deviceId !== "default" ? { exact: deviceId } : "default",
        width: 1280,
        frameRate: { ideal: 24, min: 15, max: 24 },
      };

    if (type === MediaTypes.MIC) {
      mediaDevicesConfig.audio = {
        deviceId: deviceId !== "default" ? { exact: deviceId } : "default",
      };
    }

    if (_.isEmpty(mediaDevicesConfig)) return { success: false, message: "Nenhuma configuração especificada." };

    const streamFound = await navigator.mediaDevices.getUserMedia(mediaDevicesConfig);

    if (!streamFound)
      return {
        success: true,
        data: [],
        message: "Nenhum dispositivo encontrado.",
      };

    const foundTrackId = mappedTracks.find((m) => m.kind === type);

    if (foundTrackId) {
      const removalTrack = ReturnMediaStream?.getTrackById(foundTrackId.id);

      // Remove track
      if (removalTrack) {
        removalTrack?.stop();
        ReturnMediaStream?.removeTrack(removalTrack);
      }
      mappedTracks = [...mappedTracks.filter((m) => m.id !== foundTrackId.id)];
    }

    // Add to main stream
    streamFound.getTracks().forEach((t) => {
      ReturnMediaStream?.addTrack(t);
      mappedTracks.push({ id: t.id, kind: type });
    });

    dispatch(setMediaStream());

    return { success: true, data: streamFound };
  };

  const createMediaStream = async ({ inputDeviceId, cameraDeviceId }: MediaManagerConfig) => {
    logDebug("MediaManager: criando stream");

    // Map devices
    const checkDevicesResult = await checkDevices();
    if (!checkDevicesResult.success) {
      logWarn("MediaManager: falha ao buscar dispositivos", { message: checkDevicesResult.message ?? null });
    }

    // Get local tracks
    const { success, stream } = await getAllUserMedia({
      inputDeviceId,
      cameraDeviceId,
    });

    if (!stream)
      return {
        success: false,
        message: "Não foi possível encontrar dispositivos",
      };

    //  Map tracks
    const localTracks = stream?.getTracks();
    localTracks?.forEach((track) => {
      mappedTracks.push({
        id: track.id,
        kind: track.kind === "audio" ? MediaTypes.MIC : MediaTypes.CAM,
      });
    });

    // Set stream
    ReturnMediaStream = stream;

    dispatch(setMediaStream());

    return { success: true, data: stream };
  };

  return (MediaManagerInstance = {
    createMediaStream,
    checkDevices,
    stopLocalMedia,
    getTracks,
    getMediaStream,
    changeDeviceStream,
  });
}

// Hook monitor
export function initDeviceManager() {
  const { inputDeviceId, outputDeviceId, cameraDeviceId } = useAppSelector((state) => state.media);
  const dispatch = useAppDispatch();

  // Get current inputs
  const [_inputDeviceId, setInputDeviceId] = useState(inputDeviceId);
  const [_outputDeviceId, setOutputDeviceId] = useState(outputDeviceId);
  const [_cameraDeviceId, setCameraDeviceId] = useState(cameraDeviceId);

  // Detect input changes
  useEffect(() => {
    if (inputDeviceId !== _inputDeviceId) {
      setInputDeviceId(inputDeviceId);
      MediaManagerInstance.changeDeviceStream(MediaTypes.MIC, inputDeviceId);
    }
    if (outputDeviceId !== _outputDeviceId) {
      setOutputDeviceId(outputDeviceId);
      MediaManagerInstance.changeDeviceStream(MediaTypes.AUDIO, outputDeviceId);
    }
    if (cameraDeviceId !== _cameraDeviceId) {
      setCameraDeviceId(cameraDeviceId);
      MediaManagerInstance.changeDeviceStream(MediaTypes.CAM, cameraDeviceId);
    }
  }, [inputDeviceId, outputDeviceId, cameraDeviceId]);

  useEffect(() => {
    const detectDeviceChange = async () => {
      if (MediaManagerInstance) {
        const { data: allDevices } = await MediaManagerInstance.checkDevices();

        const updateDevicesPayload: Array<{ type: string; deviceId: string }> = [];

        const foundInput = allDevices.find((d: any) => d.deviceId === _inputDeviceId && d.kind === MediaTypes.MIC);
        const foundOutput = allDevices.find((d: any) => d.deviceId === _outputDeviceId && d.kind === MediaTypes.AUDIO);
        const foundCamera = allDevices.find((d: any) => d.deviceId === _cameraDeviceId && d.kind === MediaTypes.CAM);

        if (!foundInput) updateDevicesPayload.push({ type: MediaTypes.MIC, deviceId: "default" });
        if (!foundOutput) updateDevicesPayload.push({ type: MediaTypes.AUDIO, deviceId: "default" });

        if (!foundCamera) {
          const foundAnyCamera = allDevices.find((d: any) => d.kind === MediaTypes.CAM);

          if (foundAnyCamera) updateDevicesPayload.push({ type: MediaTypes.CAM, deviceId: foundAnyCamera.id });
        }

        dispatch(updateMultipleDevices(updateDevicesPayload));
      }
    };

    const myListener = () => {
      logDebug("MediaManager: dispositivo de mídia alterado");
      // Call a function here to update your device list
      detectDeviceChange();
    };

    navigator.mediaDevices.addEventListener("devicechange", myListener);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", myListener);
    };
  }, []);

  return null;
}
